import { cn } from '@/shared/utils/cn';

type Variant = 'inline' | 'block' | 'fill';

/**
 * Loading indicator with an accessible label.
 *
 * Three sizes for three places:
 *
 * - `inline`: a small wheel with the word next to it, for a corner of something that is
 *   already on screen (a PDF being generated, a search in a picker).
 * - `block`: takes the place of the list that is coming. A big wheel, centred, in a
 *   dashed box the height of a few rows, so a screen that is loading looks like a screen
 *   that is loading and not like one with a speck above the table.
 * - `fill`: the same big wheel, centred, with no box of its own: for the inside of a
 *   `Card` (a box in a box reads as two things) and for the whole page while the session
 *   is being checked. It fills whatever the caller gives it (`min-h-64` by default).
 */
export function Spinner({
  label = 'Cargando',
  variant = 'inline',
  className,
}: {
  label?: string;
  variant?: Variant;
  className?: string;
}) {
  if (variant === 'inline') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn('text-ink-3 flex items-center gap-2 py-2', className)}
      >
        <span
          aria-hidden="true"
          className="border-line border-t-accent size-4 animate-spin rounded-full border-2"
        />
        <span className="text-sm">{label}</span>
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'text-ink-3 flex min-h-64 flex-1 flex-col items-center justify-center gap-3 px-6 py-12',
        variant === 'block' && 'border-line bg-card rounded-lg border border-dashed',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="border-line border-t-accent size-10 animate-spin rounded-full border-[3px]"
      />
      <span className="text-[14.5px]">{label}</span>
    </div>
  );
}
